import React, { useCallback, useEffect, useState } from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import FileInfoCard from '#/common/components/FileUpload/FileInfoCard';
import { useNavigate } from 'react-router-dom';
import { Attachment, ImportFileOutputData } from '#/graphql/types';
import getAttachmentQuery from '#/common/components/AttachmentUpload/hooks//useAttachmentQuery';

interface SuccessModalProps {
  visible: boolean;
  onHiding: () => void;
  importResult: ImportFileOutputData | null;
}

const SuccessModal: React.FC<SuccessModalProps> = ({ visible, onHiding, importResult }) => {
  const navigate = useNavigate();
  const { getAttachment } = getAttachmentQuery();
  const [attachmentData, setAttachmentData] = useState<Attachment | undefined>(undefined);

  useEffect(() => {
    const fetchAttachment = async () => {
      if (!importResult || !importResult.errorFileId) {
        setAttachmentData(undefined);
        return;
      }

      const resp: any = await getAttachment(importResult.errorFileId);
      setAttachmentData(resp?.data?.getAttachment?.data as Attachment | undefined);
    };

    fetchAttachment();
  }, [importResult, getAttachment]);

  const redirectToAssetList = useCallback(() => {
    onHiding();
    navigate('/assets/list');
  }, []);

  const handleDownloadFile = useCallback(async () => {
    if (attachmentData?.downloadUrl) {
      window.open(attachmentData?.downloadUrl, '_blank');
    }
    redirectToAssetList();
  }, [attachmentData]);

  const createdCount = importResult?.successfulImports ?? 0;
  const failedCount = importResult ? importResult.totalRows - importResult.successfulImports : 0;
  const totalCount = importResult?.totalRows ?? 0;

  return (
    <Modal
      visible={visible}
      title="Successful Import"
      width={640}
      height="auto"
      onHiding={onHiding}
      content={
        <>
          <div style={{ marginBottom: '16px', marginTop: '16px' }}>
            {createdCount}/{totalCount} New asset records were created.
          </div>
          <div style={{ marginBottom: '20px' }}>
            We have found issues in {failedCount}/{totalCount} asset records that need to be fixed
            before importing them again. Here is a file with the assets and their errors.
          </div>
          <FileInfoCard
            fileName={attachmentData?.originalFileName ?? 'output.csv'}
            fileSize={attachmentData?.size ?? 0}
            width="100%"
          />
        </>
      }
      actions={[
        <TMMButton key="cancel" label="Skip" type="normal" onClick={redirectToAssetList} />,
        <TMMButton key="ok" label="Download File" type="submit" onClick={handleDownloadFile} />,
      ]}
    />
  );
};

export default SuccessModal;
